import classNames from "classnames"
import type { ReactNode, FC } from "react"
import { useEffect, useRef, useState } from "react"
import Card from "../../atoms/Card"
import CardHeading from "../../atoms/CardHeading/CardHeading"
import StarRating from "../../atoms/StarRating/StarRating"
import FinePrint from "../../atoms/FinePrint/FinePrint"
import Spinner from "~/icons/Spinner"

export interface Props {
  title: string
  ctaButton: ReactNode
  ctaLoading?: boolean
  /**
   * When true, a second copy of the CTA renders in a `position: fixed` bar
   * pinned to the bottom of the viewport on mobile. The bar is hidden while
   * the in-panel CTA button is visible in the viewport, and slides/fades in
   * once that button scrolls out of view (and reverses when it scrolls back
   * into view). Desktop layout is unchanged. When false (default) only the
   * in-panel CTA renders, with no fixed bar.
   */
  ctaFixedBar?: boolean
  starRating?: { rating: number; text: string }
  subtitle?: string
  locationName?: string
  /**
   * When the location line is expected but `locationName` has not loaded yet,
   * set this to reserve the line's vertical space so the content below does not
   * shift down when the name arrives.
   */
  reserveLocationSpace?: boolean
  metadataTags?: ReactNode
  description?: string
  features?: string[]
  availabilityNotice?: ReactNode
  finePrint?: string
  className?: string
}

const ProductInfoPanel = ({
  title,
  ctaButton,
  ctaLoading,
  ctaFixedBar = false,
  starRating,
  subtitle,
  locationName,
  reserveLocationSpace,
  metadataTags,
  description,
  features,
  availabilityNotice,
  finePrint,
  className
}: Props) => {
  const shortLocationName = locationName === "5th Avenue" ? "NYC" : locationName
  const inlineCtaRef = useRef<HTMLDivElement>(null)
  // Starts true so the fixed bar stays hidden until the observer's first
  // callback fires, avoiding a flash of the bar on initial render.
  const [isInlineCtaVisible, setIsInlineCtaVisible] = useState(true)

  useEffect(() => {
    if (!ctaFixedBar) return

    const node = inlineCtaRef.current
    if (!node) return

    const observer = new IntersectionObserver(([entry]) =>
      setIsInlineCtaVisible(entry.isIntersecting)
    )
    observer.observe(node)

    return () => observer.disconnect()
  }, [ctaFixedBar])

  const ctaContent = ctaLoading ? (
    <div className="h-14 w-full rounded-full bg-camp-primary flex items-center justify-center">
      <Spinner className="w-6 h-6 animate-spin text-white" />
    </div>
  ) : (
    ctaButton
  )

  return (
    <>
      <Card className={classNames("md:max-w-[600px] shadow-sm", className)}>
        <div className="flex flex-col gap-4 p-6 md:px-10 md:py-8">
          {starRating && (
            <StarRating rating={starRating.rating} text={starRating.text} />
          )}

          <div>
            <CardHeading>{title}</CardHeading>
            {subtitle && (
              <p className="text-[20px] md:text-[18px] text-blue-dark-500 mt-1">
                {subtitle}
              </p>
            )}
          </div>

          {locationName ? (
            <div>At CAMP {shortLocationName}</div>
          ) : reserveLocationSpace ? (
            <div className="h-6" aria-hidden />
          ) : null}

          {metadataTags && <div className="my-1">{metadataTags}</div>}

          <div ref={inlineCtaRef} className="my-2">
            {ctaContent}
          </div>

          {description && (
            <div className="text-[15px] md:text-[18px] text-gray-300">
              <ActivityTypeDescription value={description} />
            </div>
          )}

          {features && features.length > 0 && (
            <ul className="list-disc list-outside text-[14px] md:text-lg text-gray-300 space-y-1 ml-4 md:ml-6">
              {features.map((feature, index) => (
                <li key={index}>{feature}</li>
              ))}
            </ul>
          )}

          {availabilityNotice && <div>{availabilityNotice}</div>}

          {finePrint && <FinePrint>{finePrint}</FinePrint>}
        </div>
      </Card>

      {ctaFixedBar && <div aria-hidden className="md:hidden h-24" />}

      {ctaFixedBar && (
        <div
          aria-hidden={isInlineCtaVisible}
          {...(isInlineCtaVisible ? ({ inert: "" } as any) : {})}
          className={classNames(
            "md:hidden fixed inset-x-0 bottom-0 z-40 bg-white px-3 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] transition-all duration-[250ms] ease-in-out",
            isInlineCtaVisible
              ? "opacity-0 translate-y-full pointer-events-none"
              : "opacity-100 translate-y-0"
          )}
        >
          {ctaContent}
        </div>
      )}
    </>
  )
}

const ActivityTypeDescription: FC<{ value: string }> = ({ value }) =>
  value.includes("<hr>") ? (
    <div className="grid gap-4 sm:grid-cols-2 lg:gap-6">
      {value.split("<hr>").map(column => (
        <div
          key={column}
          className="contentful-editor"
          dangerouslySetInnerHTML={{ __html: column }}
        />
      ))}
    </div>
  ) : (
    <div
      className="contentful-editor"
      dangerouslySetInnerHTML={{ __html: value }}
    />
  )

ProductInfoPanel.displayName = "ProductInfoPanel"

export { ProductInfoPanel }
export default ProductInfoPanel
